import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';

export interface ISmsDeliveryRecordEntityAttributes extends IModelAttributes {
	deliveryStatus: Enums.smsDeliveryStatus;
	responseId: string;

	bookingId: string;
	booking: Models.BookingEntity | Models.IBookingEntityAttributes;
	ferryTripId: string;
	ferryTrip: Models.FerryTripEntity | Models.IFerryTripEntityAttributes;
}
@entity('SmsDeliveryRecordEntity', 'Sms Delivery Record')
export default class SmsDeliveryRecordEntity extends Model implements ISmsDeliveryRecordEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The status of the SMS delivery.
	 */
	@observable
	@attribute<SmsDeliveryRecordEntity, Enums.smsDeliveryStatus>()
	public deliveryStatus: Enums.smsDeliveryStatus;

	/**
	 * The Id that is returned from DirectSms when an SMS is successfully sent. This Id will be used to find the associated booking/user that the message was sent to.
	 */
	@observable
	@attribute<SmsDeliveryRecordEntity, string>()
	public responseId: string;

	@observable
	@attribute()
	public bookingId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public booking: Models.BookingEntity;

	@observable
	@attribute()
	public ferryTripId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public ferryTrip: Models.FerryTripEntity;

	constructor(attributes?: Partial<ISmsDeliveryRecordEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ISmsDeliveryRecordEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.deliveryStatus !== undefined) {
				this.deliveryStatus = attributes.deliveryStatus;
			}
			if (attributes.responseId !== undefined) {
				this.responseId = attributes.responseId;
			}
			if (attributes.bookingId !== undefined) {
				this.bookingId = attributes.bookingId;
			}
			if (attributes.booking !== undefined) {
				if (attributes.booking === null) {
					this.booking = attributes.booking;
				} else if (attributes.booking instanceof Models.BookingEntity) {
					this.booking = attributes.booking;
					this.bookingId = attributes.booking.id;
				} else {
					this.booking = new Models.BookingEntity(attributes.booking);
					this.bookingId = this.booking.id;
				}
			}
			if (attributes.ferryTripId !== undefined) {
				this.ferryTripId = attributes.ferryTripId;
			}
			if (attributes.ferryTrip !== undefined) {
				if (attributes.ferryTrip === null) {
					this.ferryTrip = attributes.ferryTrip;
				} else if (attributes.ferryTrip instanceof Models.FerryTripEntity) {
					this.ferryTrip = attributes.ferryTrip;
					this.ferryTripId = attributes.ferryTrip.id;
				} else {
					this.ferryTrip = new Models.FerryTripEntity(attributes.ferryTrip);
					this.ferryTripId = this.ferryTrip.id;
				}
			}
		}
	}
}
